import { t } from '@web/i18n';
import { twcn } from '@web/ui/core/utils';

const RAIL_BLOCK = 'block animate-pulse rounded-md bg-border-soft';
const PAPER_BLOCK = 'block animate-pulse rounded-md bg-muted';

function Block({
  className,
  tone = 'paper',
}: Readonly<{ className: string; tone?: 'rail' | 'paper' }>): React.JSX.Element {
  return (
    <span aria-hidden className={twcn(tone === 'rail' ? RAIL_BLOCK : PAPER_BLOCK, className)} />
  );
}

/**
 * WorkspaceShellSkeleton — router `defaultPendingComponent`. Mirrors the
 * geometry of `OrgWorkspaceLayout` (rail + `Scaffold` + `AppBar`) with
 * pulsing blocks so a cold load shows the app's frame instead of a blank
 * viewport while route loaders resolve. Purely presentational: the
 * caller (the router's pending wrapper in `core`) passes the persisted
 * sidebar collapse state so the rail width does not jump when the real
 * layout mounts.
 */
export function WorkspaceShellSkeleton({
  collapsed,
}: Readonly<{ collapsed: boolean }>): React.JSX.Element {
  return (
    <div
      role="status"
      aria-label={t('workspace.loading')}
      data-testid="workspace-shell-skeleton"
      className="flex h-screen overflow-hidden"
    >
      <div
        data-testid="workspace-shell-skeleton-rail"
        className={twcn(
          'flex h-screen flex-shrink-0 flex-col gap-2 border-r border-border-soft bg-muted px-3 pt-4',
          collapsed ? 'w-rail-w-collapsed items-center' : 'w-rail-w',
        )}
      >
        {collapsed ? (
          <>
            <Block tone="rail" className="h-6 w-6" />
            <Block tone="rail" className="mt-4 h-8 w-8" />
            <Block tone="rail" className="h-8 w-8" />
            <Block tone="rail" className="h-8 w-8" />
          </>
        ) : (
          <>
            <Block tone="rail" className="h-3 w-16" />
            <Block tone="rail" className="mt-4 h-8 w-full" />
            <Block tone="rail" className="h-8 w-full" />
            <Block tone="rail" className="h-8 w-full" />
            <span aria-hidden className="my-2 block h-px w-full bg-border-soft" />
            <Block tone="rail" className="h-6 w-3/4" />
            <Block tone="rail" className="h-6 w-2/3" />
            <Block tone="rail" className="h-6 w-4/5" />
            <Block tone="rail" className="h-6 w-1/2" />
          </>
        )}
      </div>
      <div className="flex h-full min-w-0 flex-1 flex-col bg-paper">
        <div className="flex min-h-topbar-h items-center border-b border-border-soft px-6">
          <Block className="h-3 w-32" />
        </div>
        <div className="flex flex-col gap-4 px-8 py-7">
          <Block className="h-3 w-24" />
          <Block className="h-10 w-2/3" />
          {[0, 1, 2].map((i) => (
            <div key={i} className="overflow-hidden rounded-lg border border-border-soft">
              <div className="flex items-center gap-3 border-b border-border-soft bg-muted px-4 py-3">
                <Block tone="rail" className="h-row-h w-row-h" />
                <Block tone="rail" className="h-4 w-40" />
              </div>
              <Block className="m-4 h-12" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
